import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@familyzone/component-library";
import { ResourceOption, ResourceType, SelectedResource } from "./SelectedFocusResources";
import { Box, Text } from "@chakra-ui/react";
import { convertYoutubeUrlToNoCookieYoutubeUrl, isYoutubeVideoUrl } from "js/utils/urlUtils";
import { getSignaturesForFocusResourcePicker, parseDomain, searchSignatures } from "./utils";
import useStore from "stores/index";
import { colours } from "@/utils/themeUtils";

interface Props {
	addResource: (resource: SelectedResource) => void;
}

export function FocusResourcePickerInput(props: Props): JSX.Element {
	const [resourceInput, setResourceInput] = useState<string>("");
	const [resourceOptions, setResourceOptions] = useState<ResourceOption[]>([]);
	const [showOptionsDropDown, setShowOptionsDropDown] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const store = useStore();

	const availableSignatures = useMemo(() => {
		return getSignaturesForFocusResourcePicker(store.signatures);
	}, [store.signatures]);

	// Creates and cleans up an event listener for clicking outside the dropdown. This allows us to keep the dropdown open when the user clicks on options in it.
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	function handleClickOutside(event: MouseEvent) {
		if (
			inputRef.current &&
			!inputRef.current.contains(event.target as Node) &&
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setShowOptionsDropDown(false);
		}
	}

	function handleChangeResourceInput(event: ChangeEvent<HTMLInputElement>) {
		let val = event.target.value;

		setResourceInput(val);

		// if is a URL, only show one option
		if (parseDomain(val)) {
			// Clear cookies from youtube urls
			if (isYoutubeVideoUrl(val)) {
				val = convertYoutubeUrlToNoCookieYoutubeUrl(val);
			}

			const resourceOption: ResourceOption = {
				label: `Website: ${val}`,
				resourceType: ResourceType.Url,
				resourceValue: val,
			};
			setResourceOptions([resourceOption]);

			return;
		}

		prepareSignatureOptions(availableSignatures, val);
	}

	function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		setShowOptionsDropDown(true);
		if (event.key === "Escape") {
			// Prevent the event from closing the drawer
			event.stopPropagation();

			// Manually unfocus the input and hide the dropdown
			(event.target as HTMLInputElement).blur();
			setShowOptionsDropDown(false);
		}
	}

	function prepareSignatureOptions(signatures: ResourceOption[], searchTerm?: string): void {
		setResourceOptions(searchSignatures(signatures, searchTerm, 100));
	}

	function handleAddResource(selected: ResourceOption): void {
		if (selected.resourceValue === "") return;

		setResourceInput("");
		prepareSignatureOptions(availableSignatures); // reset the options

		props.addResource({
			label: selected.resourceType === ResourceType.Url ? selected.resourceValue : selected.label,
			resourceType: selected.resourceType,
			resourceValue: selected.resourceValue,
			openTab: true,
			isValid: true,
		});
	}

	useEffect(() => {
		prepareSignatureOptions(availableSignatures);
	}, [availableSignatures]);

	return (
		<Box position={"relative"}>
			<Text fontWeight="medium" mb={5}>
				Resources
			</Text>
			<Input
				ref={inputRef}
				autoComplete="off"
				placeholder={"Paste a website URL or search for applications"}
				value={resourceInput}
				onChange={handleChangeResourceInput}
				onClick={() => setShowOptionsDropDown(true)}
				data-testid="resource-input"
				onKeyDown={handleInputKeyDown}
			/>

			{showOptionsDropDown && (
				<Box
					ref={dropdownRef}
					background={"neutrals.0"}
					mt={3}
					position={"absolute"}
					width={"100%"}
					boxShadow={"0px 3px 10px rgba(0, 0, 0, 0.3)"}
					borderRadius={"md"}
					overflow={"scroll"}
					zIndex={10}
					maxHeight={"300px"}
				>
					{resourceOptions.length === 0 && (
						<Box color={"neutrals.300"} px={20} py={8}>
							No results found
						</Box>
					)}

					{resourceOptions.map((option, index) => (
						<Box
							cursor={"pointer"}
							key={index}
							onClick={() => {
								handleAddResource(option);
								setShowOptionsDropDown(false);
							}}
							_hover={{
								background: colours.neutrals[50],
							}}
							transition={"background 0.2s"}
							px={20}
							py={8}
							borderBottom={1}
						>
							<Text fontSize={""}>{option.label}</Text>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
}
